
import {
  defineComponent,
  ref,
  computed,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import SelectData from "@/components/Inputs/interface/IInputSelect";
import { ensinoListPorEscolaID } from "@/core/listas/ensinoList";

export default defineComponent({
  name: "EnsinoPorEscola",
  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "select",
    },
    label: {
      type: String,
      default: "Ensino:",
    },
    placeholder: {
      type: String,
      default: "Selecione uma das opções",
    },
    dropdownWidth: {
      type: String,
      default: "100%",
      validator: (value: string) => {
        return /^(\d+(%|px|em|rem|vh|vw))|auto$/.test(value);
      },
    },
    // NOVA PROP: escolaId para buscar ensinos automaticamente
    escolaId: {
      type: [String, Number],
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true, // Se deve buscar automaticamente
    },
  },
  emits: ["update:modelValue", "change", "ensinosLoaded"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const searchQuery = ref("");
    const dropdownRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const optionsContainer = ref<HTMLElement | null>(null);
    const isTouched = ref(false);
    const isLoading = ref(false);

    // Lista interna de ensinos
    const ensinos = ref<SelectData[]>([]);

    const visibleCount = ref(20);
    const increment = 20;

    // Função para buscar ensinos
    const pegarEnsinos = async () => {
      try {
        if (!props.escolaId) {
          console.warn("escolaId não fornecido para buscar ensinos");
          ensinos.value = [];
          return;
        }

        isLoading.value = true;
        const resultado = await ensinoListPorEscolaID(props.escolaId);

        // Mapeia os ensinos seguindo o padrão
        const mappedEnsinos = resultado.map((ensino) => ({
          value: ensino.value,
          label: ensino.label,
          text: ensino.label,
        }));

        ensinos.value = mappedEnsinos;

        // Seleção automática se houver apenas 1 ensino
        if (mappedEnsinos.length === 1 && !selectedValue.value) {
          selectedValue.value = mappedEnsinos[0].value;
          emit("change", { target: { value: mappedEnsinos[0].value } });
        }

        // Emite evento para o pai saber que os ensinos foram carregados
        emit("ensinosLoaded", mappedEnsinos);
      } catch (error) {
        console.error("Erro ao carregar ensinos:", error);
        ensinos.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // Watcher para reativar busca quando escolaId muda
    watch(
      () => props.escolaId,
      (newEscolaId, oldEscolaId) => {
        // Limpa seleção atual quando escola muda
        if (newEscolaId !== oldEscolaId && oldEscolaId !== undefined) {
          selectedValue.value = "";
          emit("change", { target: { value: "" } });
        }

        // Busca novos ensinos se autoFetch está ativo
        if (props.autoFetch && newEscolaId) {
          pegarEnsinos();
        } else if (!newEscolaId) {
          // Se não tem escola, limpa ensinos
          ensinos.value = [];
        }
      },
      { immediate: true }
    );

    watch(searchQuery, () => {
      visibleCount.value = 20;
    });

    const selectedValue = computed({
      get: () => props.modelValue,
      set: (newValue: string | number) => {
        emit("update:modelValue", newValue);
      },
    });

    // Usa ensinos internos
    const options = computed(() => {
      return ensinos.value;
    });

    const selectedLabel = computed(() => {
      if (!Array.isArray(options.value)) return "";
      if (selectedValue.value == undefined) return "";
      const selectedOption = options.value.find(
        (option) => option.value === props.modelValue
      );
      return selectedOption ? selectedOption.label : "";
    });

    const filteredOptions = computed(() => {
      return options.value.filter(({ label }) =>
        label?.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const displayedOptions = computed(() => {
      let optionsList = props.required
        ? [...filteredOptions.value]
        : [{ value: "", label: " " }, ...filteredOptions.value];

      return optionsList.slice(0, visibleCount.value);
    });

    const borderStyle = computed(() => {
      if (!isTouched.value) return "";
      if (props.required && !selectedValue.value) {
        return "1px solid red";
      } else if (!props.required) {
        return "1px solid green";
      }
      return "";
    });

    const toggleDropdown = () => {
      if (isLoading.value) return; // Não abre se está carregando

      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        searchQuery.value = "";
        visibleCount.value = 20;
        nextTick(() => {
          searchInput.value?.focus();
        });
      }
    };

    const selectOption = (value: any, label: any) => {
      selectedValue.value = value;
      emit("change", { target: { value } });
      isOpen.value = false;
    };

    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.value &&
        !dropdownRef.value.contains(event.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    const selectFirstOption = () => {
      if (filteredOptions.value.length > 0) {
        const { value, label } = filteredOptions.value[0];
        selectOption(value, label);
      }
    };

    const onScroll = () => {
      const container = optionsContainer.value;
      if (container) {
        const { scrollTop, clientHeight, scrollHeight } = container;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          if (visibleCount.value < filteredOptions.value.length) {
            visibleCount.value = Math.min(
              visibleCount.value + increment,
              filteredOptions.value.length
            );
          }
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      searchQuery,
      filteredOptions,
      displayedOptions,
      toggleDropdown,
      selectOption,
      selectedLabel,
      dropdownRef,
      searchInput,
      optionsContainer,
      selectFirstOption,
      borderStyle,
      onScroll,
      isLoading,
      pegarEnsinos, // Expõe para uso externo se necessário
    };
  },
});
